import React, { useCallback, useContext, useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';

import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { FileRef } from 'types';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import { deleteFile, uploadFile } from './file-field-api';
import Chip from '@material-ui/core/Chip';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    label: {
      padding: '0 5px',
      zIndex: 100,
      backgroundColor: '#f6f6f6',
      color: 'black',
      fontSize: 16,
      fontWeight: 'bold',
    },
    valueLabelWrapper: {
      padding: '12px 14px',
      width: '100%',
    },
    valueLabel: {
      minHeight: 32,
    },
    fieldWrapper: {
      display: 'inline-flex',
      position: 'relative',
      borderColor: 'rgba(0, 0, 0, 0.23)',
      borderStyle: 'solid',
      borderWidth: 1,
      borderRadius: 4,
    },
    uploadIcon: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      right: 8,
    },
    chip: {
      marginRight: 4,
      marginBottom: 5,
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
    },
    chipDeleteIcon: {
      color: '#fff',
      width: 20,
      height: 20,
      '&:hover': {
        color: '#fff',
        cursor: 'pointer',
      },
    },
  })
);

export interface CommonFieldProps {
  /**
   * Name of input.
   */
  name: string;

  /**
   * Label of input.
   */
  label?: string;
}

type MultipleFileField = {
  value: FileRef[] | undefined;
  multiple: true;
  onChange: (value: FileRef[] | undefined) => void;
};

type SingleFileField = {
  value: FileRef | undefined;
  multiple?: false;
  onChange: (value: FileRef | undefined) => void;
};

export const FileField = (
  props: CommonFieldProps & (SingleFileField | MultipleFileField)
) => {
  const classes = useStyles();
  const { showNotification } = useContext(NotificationCtx);

  // Uploading flag.
  const [uploading, setUploading] = useState(false);

  // Delete file handler.
  const handleDeleteFile = useCallback(
    (id: string) => async () => {
      try {
        await deleteFile(id);
        if (props.multiple) {
          props.onChange((props.value ?? []).filter((f) => f.id !== id));
        } else {
          props.onChange(undefined);
        }
      } catch (e) {
        showNotification(
          'Soubor se nezdařilo smazat.',
          NotificationVariant.ERROR
        );
      }
    },
    [props, showNotification]
  );

  // Change handler
  const handleChange = useCallback(
    async (event: React.ChangeEvent<any>) => {
      const files = event.currentTarget.files[0];

      setUploading(true);
      try {
        const fileRef = await uploadFile(files);

        if (props.multiple) {
          props.onChange([...(props.value ?? []), fileRef]);
        } else {
          props.onChange(fileRef);
        }
      } catch (e) {
        showNotification(
          'Soubor se nezdařilo nahrát.',
          NotificationVariant.ERROR
        );
      } finally {
        setUploading(false);
      }
    },
    [props, showNotification]
  );

  const valueLabel = props.multiple ? (
    props.value?.map((f) => (
      <Chip
        key={f.name}
        label={f.name}
        onDelete={handleDeleteFile(f.id)}
        className={classes.chip}
        classes={{ deleteIcon: classes.chipDeleteIcon }}
      />
    ))
  ) : props.value ? (
    <Chip
      label={props.value.name}
      onDelete={handleDeleteFile(props.value.id)}
      className={classes.chip}
      classes={{ deleteIcon: classes.chipDeleteIcon }}
    />
  ) : (
    <></>
  );

  return (
    <FormControl variant="outlined" className={classes.root}>
      <InputLabel className={classes.label} shrink>
        {props.label}
      </InputLabel>
      <div className={classes.fieldWrapper}>
        <div className={classes.valueLabelWrapper}>
          <div className={classes.valueLabel}>{valueLabel}</div>
        </div>
        <div className={classes.uploadIcon}>
          <IconButton
            size="small"
            component="label"
            disabled={(!props.multiple && !!props.value) || uploading}
          >
            <CloudUploadIcon />
            <input
              type="file"
              name={props.name}
              multiple={props.multiple}
              style={{ display: 'none' }}
              onChange={handleChange}
            />
          </IconButton>
        </div>
      </div>
    </FormControl>
  );
};
